
import { AIModule } from '../../types';

export const AUTONOMOUS_SYSTEMS_MODULE_3: AIModule = {
  id: 'path-planning-navigation',
  title: 'Path Planning & Navigation',
  description: 'Master algorithms for autonomous navigation including global path planning, local obstacle avoidance, and dynamic re-planning.',
  duration: '5 hours',
  difficulty: 'hard',
  content: `
# Path Planning & Navigation

Path planning is the computational problem of finding a sequence of valid configurations that moves a robot from a source to a destination, while avoiding obstacles and optimizing various criteria.

## Types of Path Planning

### Global Path Planning
- **Complete map knowledge**: Plans entire route before movement
- **Optimal solutions**: Finds shortest or safest path
- **Static environment**: Assumes obstacles don't change
- **Examples**: A*, Dijkstra, RRT

### Local Path Planning
- **Limited sensing range**: Plans based on immediate surroundings
- **Real-time execution**: Reactive to dynamic obstacles
- **Safety-focused**: Collision avoidance priority
- **Examples**: Dynamic Window Approach, Vector Field Histogram

### Hybrid Approaches
- **Global + Local**: Combines long-term and short-term planning
- **Hierarchical planning**: Multiple planning levels
- **Adaptive replanning**: Updates global plan when needed

## Advanced Path Planning Algorithms

\`\`\`python
import numpy as np
import matplotlib.pyplot as plt
from typing import List, Tuple, Dict, Optional, Set
from dataclasses import dataclass
import heapq
import random
import math
from collections import deque
from scipy.spatial import KDTree
from scipy.interpolate import interp1d

@dataclass
class Point:
    x: float
    y: float
    
    def distance_to(self, other: 'Point') -> float:
        return math.sqrt((self.x - other.x)**2 + (self.y - other.y)**2)
    
    def __hash__(self):
        return hash((round(self.x, 3), round(self.y, 3)))
    
    def __eq__(self, other):
        return abs(self.x - other.x) < 0.001 and abs(self.y - other.y) < 0.001

@dataclass
class Obstacle:
    center: Point
    radius: float
    
    def contains_point(self, point: Point) -> bool:
        return point.distance_to(self.center) <= self.radius

@dataclass
class RobotState:
    position: Point
    velocity: Point
    heading: float  # radians
    
class Environment:
    """Robot environment with obstacles and boundaries"""
    
    def __init__(self, width: float, height: float):
        self.width = width
        self.height = height
        self.obstacles: List[Obstacle] = []
        self.dynamic_obstacles: List[Dict] = []  # Moving obstacles
        
    def add_obstacle(self, center: Point, radius: float):
        """Add static obstacle"""
        self.obstacles.append(Obstacle(center, radius))
    
    def add_dynamic_obstacle(self, center: Point, radius: float, velocity: Point):
        """Add moving obstacle"""
        self.dynamic_obstacles.append({
            'center': center,
            'radius': radius,
            'velocity': velocity,
            'trajectory': [center]
        })
    
    def update_dynamic_obstacles(self, dt: float):
        """Update positions of moving obstacles"""
        for obs in self.dynamic_obstacles:
            new_center = Point(
                obs['center'].x + obs['velocity'].x * dt,
                obs['center'].y + obs['velocity'].y * dt
            )
            obs['center'] = new_center
            obs['trajectory'].append(new_center)
    
    def is_collision_free(self, point: Point, robot_radius: float = 0.5) -> bool:
        """Check if point is collision-free"""
        # Check boundaries
        if (point.x < robot_radius or point.x > self.width - robot_radius or
            point.y < robot_radius or point.y > self.height - robot_radius):
            return False
        
        # Check static obstacles
        for obstacle in self.obstacles:
            if point.distance_to(obstacle.center) < obstacle.radius + robot_radius:
                return False
        
        # Check dynamic obstacles
        for obs in self.dynamic_obstacles:
            if point.distance_to(obs['center']) < obs['radius'] + robot_radius:
                return False
        
        return True
    
    def is_path_collision_free(self, start: Point, end: Point, robot_radius: float = 0.5,
                              num_checks: int = 10) -> bool:
        """Check if straight line path is collision-free"""
        for i in range(num_checks + 1):
            t = i / num_checks
            check_point = Point(
                start.x + t * (end.x - start.x),
                start.y + t * (end.y - start.y)
            )
            if not self.is_collision_free(check_point, robot_radius):
                return False
        return True

class AStarPlanner:
    """A* path planning algorithm with optimizations"""
    
    def __init__(self, environment: Environment, grid_resolution: float = 1.0):
        self.env = environment
        self.grid_resolution = grid_resolution
        self.robot_radius = 0.5
        
    def plan(self, start: Point, goal: Point) -> Optional[List[Point]]:
        """Plan path from start to goal using A*"""
        
        # Convert to grid coordinates
        start_grid = self._world_to_grid(start)
        goal_grid = self._world_to_grid(goal)
        
        # A* algorithm
        open_set = []
        heapq.heappush(open_set, (0, start_grid))
        
        came_from = {}
        g_score = {start_grid: 0}
        f_score = {start_grid: self._heuristic(start_grid, goal_grid)}
        
        while open_set:
            current = heapq.heappop(open_set)[1]
            
            if current == goal_grid:
                # Reconstruct path
                path = self._reconstruct_path(came_from, current)
                # Convert back to world coordinates and smooth
                world_path = [self._grid_to_world(p) for p in path]
                return self._smooth_path(world_path)
            
            for neighbor in self._get_neighbors(current):
                if not self._is_valid_grid_point(neighbor):
                    continue
                
                tentative_g_score = g_score[current] + self._distance(current, neighbor)
                
                if neighbor not in g_score or tentative_g_score < g_score[neighbor]:
                    came_from[neighbor] = current
                    g_score[neighbor] = tentative_g_score
                    f_score[neighbor] = g_score[neighbor] + self._heuristic(neighbor, goal_grid)
                    
                    if (f_score[neighbor], neighbor) not in open_set:
                        heapq.heappush(open_set, (f_score[neighbor], neighbor))
        
        return None  # No path found
    
    def _world_to_grid(self, point: Point) -> Tuple[int, int]:
        """Convert world coordinates to grid coordinates"""
        return (int(point.x / self.grid_resolution), int(point.y / self.grid_resolution))
    
    def _grid_to_world(self, grid_point: Tuple[int, int]) -> Point:
        """Convert grid coordinates to world coordinates"""
        return Point(grid_point[0] * self.grid_resolution, grid_point[1] * self.grid_resolution)
    
    def _heuristic(self, a: Tuple[int, int], b: Tuple[int, int]) -> float:
        """Euclidean distance heuristic"""
        return math.sqrt((a[0] - b[0])**2 + (a[1] - b[1])**2)
    
    def _distance(self, a: Tuple[int, int], b: Tuple[int, int]) -> float:
        """Distance between grid points"""
        return math.sqrt((a[0] - b[0])**2 + (a[1] - b[1])**2)
    
    def _get_neighbors(self, point: Tuple[int, int]) -> List[Tuple[int, int]]:
        """Get 8-connected neighbors"""
        x, y = point
        neighbors = []
        for dx in [-1, 0, 1]:
            for dy in [-1, 0, 1]:
                if dx == 0 and dy == 0:
                    continue
                neighbors.append((x + dx, y + dy))
        return neighbors
    
    def _is_valid_grid_point(self, grid_point: Tuple[int, int]) -> bool:
        """Check if grid point is valid and collision-free"""
        world_point = self._grid_to_world(grid_point)
        return self.env.is_collision_free(world_point, self.robot_radius)
    
    def _reconstruct_path(self, came_from: Dict, current: Tuple[int, int]) -> List[Tuple[int, int]]:
        """Reconstruct path from came_from map"""
        path = [current]
        while current in came_from:
            current = came_from[current]
            path.append(current)
        return path[::-1]
    
    def _smooth_path(self, path: List[Point]) -> List[Point]:
        """Smooth path by removing unnecessary waypoints"""
        if len(path) <= 2:
            return path
        
        smoothed = [path[0]]
        i = 0
        
        while i < len(path) - 1:
            j = len(path) - 1
            # Find farthest reachable point
            while j > i + 1:
                if self.env.is_path_collision_free(path[i], path[j], self.robot_radius):
                    smoothed.append(path[j])
                    i = j
                    break
                j -= 1
            else:
                # If no skip possible, move to next point
                smoothed.append(path[i + 1])
                i += 1
        
        return smoothed

class RRTPlanner:
    """Rapidly-exploring Random Tree path planner"""
    
    def __init__(self, environment: Environment, max_iterations: int = 5000,
                 step_size: float = 2.0):
        self.env = environment
        self.max_iterations = max_iterations
        self.step_size = step_size
        self.robot_radius = 0.5
        
    def plan(self, start: Point, goal: Point, goal_bias: float = 0.1) -> Optional[List[Point]]:
        """Plan path using RRT algorithm"""
        
        # Initialize tree
        tree = {start: None}  # node -> parent
        
        for iteration in range(self.max_iterations):
            # Sample random point (with goal bias)
            if random.random() < goal_bias:
                rand_point = goal
            else:
                rand_point = Point(
                    random.uniform(0, self.env.width),
                    random.uniform(0, self.env.height)
                )
            
            # Find nearest node in tree
            nearest_node = min(tree.keys(), key=lambda n: n.distance_to(rand_point))
            
            # Extend towards random point
            new_node = self._extend_towards(nearest_node, rand_point)
            
            # Check if new node is valid
            if (new_node and 
                self.env.is_collision_free(new_node, self.robot_radius) and
                self.env.is_path_collision_free(nearest_node, new_node, self.robot_radius)):
                
                tree[new_node] = nearest_node
                
                # Check if goal is reached
                if new_node.distance_to(goal) < self.step_size:
                    # Try to connect to goal
                    if self.env.is_path_collision_free(new_node, goal, self.robot_radius):
                        tree[goal] = new_node
                        return self._extract_path(tree, start, goal)
        
        return None  # No path found
    
    def _extend_towards(self, from_point: Point, to_point: Point) -> Optional[Point]:
        """Extend from_point towards to_point by step_size"""
        distance = from_point.distance_to(to_point)
        if distance < 0.001:  # Points are too close
            return None
        
        # Calculate unit vector
        dx = (to_point.x - from_point.x) / distance
        dy = (to_point.y - from_point.y) / distance
        
        # Extend by step_size
        new_x = from_point.x + dx * min(self.step_size, distance)
        new_y = from_point.y + dy * min(self.step_size, distance)
        
        return Point(new_x, new_y)
    
    def _extract_path(self, tree: Dict[Point, Optional[Point]], 
                     start: Point, goal: Point) -> List[Point]:
        """Extract path from tree"""
        path = []
        current = goal
        
        while current is not None:
            path.append(current)
            current = tree[current]
        
        return path[::-1]

class DynamicWindowApproach:
    """Dynamic Window Approach for local path planning"""
    
    def __init__(self, max_velocity: float = 2.0, max_angular_velocity: float = 1.0,
                 acceleration: float = 1.0, angular_acceleration: float = 0.5,
                 dt: float = 0.1, prediction_time: float = 2.0):
        self.max_velocity = max_velocity
        self.max_angular_velocity = max_angular_velocity
        self.acceleration = acceleration
        self.angular_acceleration = angular_acceleration
        self.dt = dt
        self.prediction_time = prediction_time
        
        # Scoring weights
        self.heading_weight = 0.4
        self.distance_weight = 0.3
        self.velocity_weight = 0.3
        
    def plan(self, robot_state: RobotState, goal: Point, 
            environment: Environment) -> Tuple[float, float]:
        """Plan local trajectory using DWA"""
        
        # Calculate dynamic window
        velocity_window = self._calculate_dynamic_window(robot_state)
        
        best_score = -float('inf')
        best_velocity = (0, 0)
        
        # Sample velocity space
        v_samples = np.linspace(velocity_window[0], velocity_window[1], 10)
        w_samples = np.linspace(velocity_window[2], velocity_window[3], 10)
        
        for v in v_samples:
            for w in w_samples:
                # Predict trajectory
                trajectory = self._predict_trajectory(robot_state, v, w)
                
                # Check collision
                if self._is_trajectory_safe(trajectory, environment):
                    # Calculate score
                    score = self._evaluate_trajectory(trajectory, goal, v)
                    
                    if score > best_score:
                        best_score = score
                        best_velocity = (v, w)
        
        return best_velocity
    
    def _calculate_dynamic_window(self, robot_state: RobotState) -> Tuple[float, float, float, float]:
        """Calculate dynamic window of feasible velocities"""
        current_v = math.sqrt(robot_state.velocity.x**2 + robot_state.velocity.y**2)
        current_w = 0  # Simplified - would need angular velocity in state
        
        # Velocity limits
        v_min = max(0, current_v - self.acceleration * self.dt)
        v_max = min(self.max_velocity, current_v + self.acceleration * self.dt)
        
        # Angular velocity limits
        w_min = max(-self.max_angular_velocity, current_w - self.angular_acceleration * self.dt)
        w_max = min(self.max_angular_velocity, current_w + self.angular_acceleration * self.dt)
        
        return (v_min, v_max, w_min, w_max)
    
    def _predict_trajectory(self, robot_state: RobotState, 
                          velocity: float, angular_velocity: float) -> List[Point]:
        """Predict robot trajectory given velocities"""
        trajectory = []
        
        x, y, theta = robot_state.position.x, robot_state.position.y, robot_state.heading
        
        for _ in range(int(self.prediction_time / self.dt)):
            x += velocity * math.cos(theta) * self.dt
            y += velocity * math.sin(theta) * self.dt
            theta += angular_velocity * self.dt
            
            trajectory.append(Point(x, y))
        
        return trajectory
    
    def _is_trajectory_safe(self, trajectory: List[Point], environment: Environment) -> bool:
        """Check if trajectory is collision-free"""
        for point in trajectory:
            if not environment.is_collision_free(point, 0.5):  # robot radius
                return False
        return True
    
    def _evaluate_trajectory(self, trajectory: List[Point], goal: Point, velocity: float) -> float:
        """Evaluate trajectory quality"""
        if not trajectory:
            return -float('inf')
        
        # Heading score - how well trajectory heads towards goal
        end_point = trajectory[-1]
        goal_heading = math.atan2(goal.y - end_point.y, goal.x - end_point.x)
        trajectory_heading = math.atan2(
            trajectory[-1].y - trajectory[0].y,
            trajectory[-1].x - trajectory[0].x
        )
        heading_score = math.cos(goal_heading - trajectory_heading)
        
        # Distance score - how close trajectory gets to goal
        min_distance = min(point.distance_to(goal) for point in trajectory)
        distance_score = 1.0 / (1.0 + min_distance)
        
        # Velocity score - prefer higher velocities
        velocity_score = velocity / self.max_velocity
        
        # Combined score
        total_score = (self.heading_weight * heading_score +
                      self.distance_weight * distance_score +
                      self.velocity_weight * velocity_score)
        
        return total_score

class HybridAStarPlanner:
    """Hybrid A* planner for vehicles with kinematic constraints"""
    
    def __init__(self, environment: Environment, vehicle_length: float = 2.0,
                 min_turning_radius: float = 3.0):
        self.env = environment
        self.vehicle_length = vehicle_length
        self.min_turning_radius = min_turning_radius
        self.grid_resolution = 1.0
        self.angle_resolution = math.pi / 8  # 22.5 degrees
        
    def plan(self, start_state: RobotState, goal_state: RobotState) -> Optional[List[RobotState]]:
        """Plan path considering vehicle kinematics"""
        
        # Convert states to grid representation
        start_node = self._state_to_node(start_state)
        goal_node = self._state_to_node(goal_state)
        
        # A* with kinematic constraints
        open_set = []
        heapq.heappush(open_set, (0, start_node))
        
        came_from = {}
        g_score = {start_node: 0}
        f_score = {start_node: self._heuristic_cost(start_node, goal_node)}
        
        while open_set:
            current = heapq.heappop(open_set)[1]
            
            if self._is_goal_reached(current, goal_node):
                # Reconstruct path
                path_nodes = self._reconstruct_path(came_from, current)
                return [self._node_to_state(node) for node in path_nodes]
            
            # Generate kinematically feasible successors
            for successor in self._get_successors(current):
                if not self._is_valid_state(successor):
                    continue
                
                tentative_g_score = g_score[current] + self._transition_cost(current, successor)
                
                if successor not in g_score or tentative_g_score < g_score[successor]:
                    came_from[successor] = current
                    g_score[successor] = tentative_g_score
                    f_score[successor] = g_score[successor] + self._heuristic_cost(successor, goal_node)
                    
                    if (f_score[successor], successor) not in open_set:
                        heapq.heappush(open_set, (f_score[successor], successor))
        
        return None
    
    def _state_to_node(self, state: RobotState) -> Tuple[int, int, int]:
        """Convert robot state to discrete node"""
        x = int(state.position.x / self.grid_resolution)
        y = int(state.position.y / self.grid_resolution)
        theta = int(state.heading / self.angle_resolution)
        return (x, y, theta)
    
    def _node_to_state(self, node: Tuple[int, int, int]) -> RobotState:
        """Convert discrete node to robot state"""
        x, y, theta_idx = node
        return RobotState(
            position=Point(x * self.grid_resolution, y * self.grid_resolution),
            velocity=Point(0, 0),  # Simplified
            heading=theta_idx * self.angle_resolution
        )
    
    def _get_successors(self, node: Tuple[int, int, int]) -> List[Tuple[int, int, int]]:
        """Generate kinematically feasible successor nodes"""
        x, y, theta_idx = node
        theta = theta_idx * self.angle_resolution
        
        successors = []
        
        # Different motion primitives
        motions = [
            (1.0, 0),      # Forward
            (1.0, 0.5),    # Forward + left turn
            (1.0, -0.5),   # Forward + right turn
            (-0.5, 0),     # Backward (reduced)
        ]
        
        for distance, steering_angle in motions:
            # Calculate new position using bicycle model
            if abs(steering_angle) < 0.01:  # Straight motion
                new_x = x + distance * math.cos(theta)
                new_y = y + distance * math.sin(theta)
                new_theta = theta
            else:
                # Circular arc motion
                turning_radius = self.vehicle_length / math.tan(abs(steering_angle))
                turning_radius = max(turning_radius, self.min_turning_radius)
                
                angular_change = distance / turning_radius
                if steering_angle < 0:
                    angular_change = -angular_change
                
                new_x = x + turning_radius * (math.sin(theta + angular_change) - math.sin(theta))
                new_y = y - turning_radius * (math.cos(theta + angular_change) - math.cos(theta))
                new_theta = theta + angular_change
            
            # Convert to grid coordinates
            new_x_grid = int(new_x / self.grid_resolution)
            new_y_grid = int(new_y / self.grid_resolution)
            new_theta_idx = int(new_theta / self.angle_resolution) % int(2 * math.pi / self.angle_resolution)
            
            successors.append((new_x_grid, new_y_grid, new_theta_idx))
        
        return successors
    
    def _is_valid_state(self, node: Tuple[int, int, int]) -> bool:
        """Check if state is valid (collision-free)"""
        state = self._node_to_state(node)
        return self.env.is_collision_free(state.position, 1.0)  # Vehicle footprint
    
    def _heuristic_cost(self, node1: Tuple[int, int, int], node2: Tuple[int, int, int]) -> float:
        """Heuristic cost between nodes"""
        x1, y1, _ = node1
        x2, y2, _ = node2
        return math.sqrt((x2 - x1)**2 + (y2 - y1)**2)
    
    def _transition_cost(self, node1: Tuple[int, int, int], node2: Tuple[int, int, int]) -> float:
        """Cost of transition between nodes"""
        return self._heuristic_cost(node1, node2)
    
    def _is_goal_reached(self, current: Tuple[int, int, int], goal: Tuple[int, int, int]) -> bool:
        """Check if goal is reached within tolerance"""
        distance = self._heuristic_cost(current, goal)
        return distance < 2.0  # Grid units
    
    def _reconstruct_path(self, came_from: Dict, current: Tuple[int, int, int]) -> List[Tuple[int, int, int]]:
        """Reconstruct path from came_from map"""
        path = [current]
        while current in came_from:
            current = came_from[current]
            path.append(current)
        return path[::-1]

# Example usage and demonstration
def demonstrate_path_planning():
    """Demonstrate various path planning algorithms"""
    
    print("=== Path Planning Algorithms Demo ===")
    
    # Create environment
    env = Environment(20, 20)
    
    # Add obstacles
    env.add_obstacle(Point(5, 5), 2)
    env.add_obstacle(Point(15, 10), 1.5)
    env.add_obstacle(Point(10, 15), 2.5)
    env.add_dynamic_obstacle(Point(8, 8), 1, Point(0.5, 0))
    
    start = Point(1, 1)
    goal = Point(18, 18)
    
    print(f"Planning from ({start.x}, {start.y}) to ({goal.x}, {goal.y})")
    
    # Test A* planner
    print("\\n--- A* Planner ---")
    astar = AStarPlanner(env, grid_resolution=0.5)
    astar_path = astar.plan(start, goal)
    
    if astar_path:
        print(f"A* found path with {len(astar_path)} waypoints")
        path_length = sum(astar_path[i].distance_to(astar_path[i+1]) 
                         for i in range(len(astar_path)-1))
        print(f"Path length: {path_length:.2f}")
    else:
        print("A* failed to find path")
    
    # Test RRT planner
    print("\\n--- RRT Planner ---")
    rrt = RRTPlanner(env, max_iterations=1000)
    rrt_path = rrt.plan(start, goal)
    
    if rrt_path:
        print(f"RRT found path with {len(rrt_path)} waypoints")
        path_length = sum(rrt_path[i].distance_to(rrt_path[i+1]) 
                         for i in range(len(rrt_path)-1))
        print(f"Path length: {path_length:.2f}")
    else:
        print("RRT failed to find path")
    
    # Test Dynamic Window Approach
    print("\\n--- Dynamic Window Approach ---")
    robot_state = RobotState(
        position=Point(5, 5),
        velocity=Point(1, 0),
        heading=0
    )
    
    dwa = DynamicWindowApproach()
    local_goal = Point(10, 10)
    
    velocity_cmd = dwa.plan(robot_state, local_goal, env)
    print(f"DWA recommends velocity: v={velocity_cmd[0]:.2f}, ω={velocity_cmd[1]:.2f}")
    
    # Test Hybrid A*
    print("\\n--- Hybrid A* Planner ---")
    start_state = RobotState(Point(2, 2), Point(0, 0), 0)
    goal_state = RobotState(Point(17, 17), Point(0, 0), 0)
    
    hybrid_astar = HybridAStarPlanner(env)
    hybrid_path = hybrid_astar.plan(start_state, goal_state)
    
    if hybrid_path:
        print(f"Hybrid A* found path with {len(hybrid_path)} states")
        total_distance = sum(hybrid_path[i].position.distance_to(hybrid_path[i+1].position) 
                           for i in range(len(hybrid_path)-1))
        print(f"Path length: {total_distance:.2f}")
    else:
        print("Hybrid A* failed to find path")
    
    # Performance comparison
    print("\\n=== Performance Comparison ===")
    
    algorithms = [
        ("A*", lambda: astar.plan(start, goal)),
        ("RRT", lambda: rrt.plan(start, goal))
    ]
    
    for name, planner_func in algorithms:
        import time
        start_time = time.time()
        path = planner_func()
        planning_time = time.time() - start_time
        
        if path:
            path_length = sum(path[i].distance_to(path[i+1]) 
                            for i in range(len(path)-1))
            print(f"{name}: {planning_time*1000:.1f}ms, {len(path)} waypoints, {path_length:.2f} length")
        else:
            print(f"{name}: {planning_time*1000:.1f}ms, No path found")

# Run demonstration
demonstrate_path_planning()
\`\`\`

## Real-World Considerations

### Dynamic Environments
- Real-time obstacle detection and avoidance
- Replanning strategies for changing environments
- Predictive modeling of moving obstacles

### Uncertainty Handling
- Probabilistic path planning
- Robust planning under uncertainty
- Sensor noise compensation

### Multi-Robot Planning
- Centralized vs. decentralized planning
- Conflict resolution strategies
- Communication protocols

### Computational Efficiency
- Anytime algorithms for real-time constraints
- Hierarchical planning for large environments
- GPU acceleration for complex scenarios

Path planning is essential for autonomous navigation, enabling robots to move safely and efficiently through complex environments while adapting to dynamic conditions.
  `,
  quiz: {
    id: 'path-planning-navigation-quiz',
    title: 'Path Planning & Navigation Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What is the main advantage of RRT over A* for path planning?',
        type: 'multiple-choice',
        options: [
          'RRT always finds shorter paths',
          'RRT can handle continuous state spaces and high-dimensional problems',
          'RRT is faster than A*',
          'RRT guarantees optimal solutions'
        ],
        correctAnswer: 1,
        explanation: 'RRT can efficiently explore continuous and high-dimensional state spaces without requiring discretization, making it suitable for complex robotic systems.',
        difficulty: 'hard',
        points: 20
      },
      {
        id: 'q2',
        question: 'What does the Dynamic Window Approach optimize for local planning?',
        type: 'multiple-choice',
        options: [
          'Global path length',
          'Velocity commands considering robot dynamics and obstacle avoidance',
          'Memory usage',
          'Map accuracy'
        ],
        correctAnswer: 1,
        explanation: 'DWA selects velocity commands by considering robot dynamics constraints and evaluating trajectories for obstacle avoidance and goal reaching.',
        difficulty: 'hard',
        points: 15
      }
    ]
  }
};
